import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogTrigger,
  DialogFooter 
} from '../components/ui/dialog';
import { 
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { 
  Plus, 
  Edit, 
  Trash2, 
  Package,
  Euro,
  FolderOpen
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Materials() {
  const [materials, setMaterials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [editMaterial, setEditMaterial] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  
  const [formData, setFormData] = useState({
    name: '',
    price: '',
    unit: 'unidad',
    category: ''
  });

  useEffect(() => {
    fetchMaterials();
  }, []);

  const fetchMaterials = async () => {
    try {
      const response = await axios.get(`${API}/materials`);
      setMaterials(response.data);
    } catch (error) {
      toast.error('Error al cargar los materiales');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...formData,
        price: parseFloat(formData.price)
      };
      
      if (editMaterial) {
        await axios.put(`${API}/materials/${editMaterial.id}`, data);
        toast.success('Material actualizado');
      } else {
        await axios.post(`${API}/materials`, data);
        toast.success('Material añadido');
      }
      
      setIsAddDialogOpen(false);
      setEditMaterial(null);
      setFormData({ name: '', price: '', unit: 'unidad', category: '' });
      fetchMaterials();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    }
  };

  const handleEdit = (material) => {
    setEditMaterial(material);
    setFormData({
      name: material.name,
      price: material.price.toString(),
      unit: material.unit,
      category: material.category || ''
    });
    setIsAddDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await axios.delete(`${API}/materials/${deleteId}`);
      toast.success('Material eliminado');
      setDeleteId(null);
      fetchMaterials();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar');
    }
  };

  const handleCloseDialog = (open) => {
    if (!open) {
      setIsAddDialogOpen(false);
      setEditMaterial(null);
      setFormData({ name: '', price: '', unit: 'unidad', category: '' });
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'EUR'
    }).format(value);
  };

  // Group materials by category and sort alphabetically
  const materialsByCategory = materials.reduce((acc, mat) => {
    const cat = mat.category || 'Sin categoría';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(mat);
    return acc;
  }, {});

  // Sort categories alphabetically (with "Sin categoría" at the end)
  const sortedCategories = Object.keys(materialsByCategory).sort((a, b) => {
    if (a === 'Sin categoría') return 1;
    if (b === 'Sin categoría') return -1;
    return a.localeCompare(b, 'es');
  });

  // Sort materials within each category alphabetically
  sortedCategories.forEach(cat => {
    materialsByCategory[cat].sort((a, b) => a.name.localeCompare(b.name, 'es'));
  });

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="materials-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-4xl font-bold font-['Barlow_Condensed'] tracking-tight text-slate-900">
            Materiales
          </h1>
          <p className="text-slate-500 mt-1">Gestiona los materiales y sus precios</p>
        </div>
        
        <Dialog open={isAddDialogOpen} onOpenChange={handleCloseDialog}>
          <DialogTrigger asChild>
            <Button 
              className="bg-orange-500 hover:bg-orange-600 text-white gap-2"
              onClick={() => setIsAddDialogOpen(true)}
              data-testid="add-material-btn"
            >
              <Plus size={18} />
              Añadir Material
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="text-xl font-['Barlow_Condensed']">
                {editMaterial ? 'Editar Material' : 'Nuevo Material'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">Nombre</Label>
                <Input
                  id="name"
                  placeholder="Ej: Cámara 29"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  required
                  data-testid="material-name-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="price">Precio</Label>
                  <div className="relative">
                    <Euro className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
                    <Input
                      id="price"
                      type="number"
                      step="0.01"
                      min="0"
                      placeholder="0.00"
                      className="pl-9"
                      value={formData.price}
                      onChange={(e) => setFormData({...formData, price: e.target.value})}
                      required
                      data-testid="material-price-input"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="unit">Unidad</Label>
                  <Input
                    id="unit"
                    placeholder="Ej: unidad, metros, ml"
                    value={formData.unit}
                    onChange={(e) => setFormData({...formData, unit: e.target.value})}
                    data-testid="material-unit-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="category">Categoría (opcional)</Label>
                <Input
                  id="category"
                  placeholder="Ej: Ruedas, Frenos, Transmisión"
                  value={formData.category}
                  onChange={(e) => setFormData({...formData, category: e.target.value})}
                  data-testid="material-category-input"
                />
              </div>
              <DialogFooter>
                <Button type="submit" className="bg-orange-500 hover:bg-orange-600 text-white" data-testid="save-material-btn">
                  {editMaterial ? 'Guardar Cambios' : 'Añadir Material'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Materials grouped by category */}
      <div className="space-y-6" data-testid="materials-list">
        {loading ? (
          <Card className="border-slate-200">
            <CardContent className="p-0">
              <div className="flex items-center justify-center h-64">
                <div className="spinner w-8 h-8" />
              </div>
            </CardContent>
          </Card>
        ) : materials.length === 0 ? (
          <Card className="border-slate-200">
            <CardContent className="p-0">
              <div className="flex flex-col items-center justify-center h-64 text-slate-400">
                <Package size={48} className="mb-4" />
                <p>No hay materiales registrados</p>
                <p className="text-sm">Añade tu primer material para empezar</p>
              </div>
            </CardContent>
          </Card>
        ) : (
          sortedCategories.map((category) => (
            <Card key={category} className="border-slate-200" data-testid={`category-${category}`}>
              <CardHeader className="pb-3 bg-slate-50 border-b">
                <CardTitle className="flex items-center gap-2 text-lg font-['Barlow_Condensed']">
                  <FolderOpen className="w-5 h-5 text-orange-500" />
                  {category}
                  <Badge variant="outline" className="ml-2 bg-white">
                    {materialsByCategory[category].length}
                  </Badge>
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-50/50">
                        <TableHead className="text-xs font-bold uppercase text-slate-500">Nombre</TableHead>
                        <TableHead className="text-xs font-bold uppercase text-slate-500">Precio</TableHead>
                        <TableHead className="text-xs font-bold uppercase text-slate-500">Unidad</TableHead>
                        <TableHead className="text-xs font-bold uppercase text-slate-500 text-right">Acciones</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {materialsByCategory[category].map((material) => (
                        <TableRow key={material.id} className="table-row-hover" data-testid={`material-row-${material.id}`}>
                          <TableCell className="font-medium text-slate-900">{material.name}</TableCell>
                          <TableCell className="font-semibold text-orange-600">
                            {formatCurrency(material.price)}
                          </TableCell>
                          <TableCell className="text-slate-500">{material.unit}</TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-2">
                              <Button 
                                variant="outline" 
                                size="sm"
                                onClick={() => handleEdit(material)}
                                className="gap-1"
                                data-testid={`edit-material-${material.id}`}
                              >
                                <Edit size={14} />
                                Editar
                              </Button>
                              <Button 
                                variant="outline" 
                                size="sm"
                                className="text-red-500 hover:text-red-600 hover:bg-red-50"
                                onClick={() => setDeleteId(material.id)}
                                data-testid={`delete-material-${material.id}`}
                              >
                                <Trash2 size={14} />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Stats */}
      {materials.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="border-slate-200">
            <CardContent className="p-4">
              <p className="text-xs font-semibold uppercase text-slate-500">Total Materiales</p>
              <p className="text-2xl font-bold text-slate-900 mt-1 font-['Barlow_Condensed']">{materials.length}</p>
            </CardContent>
          </Card>
          <Card className="border-slate-200">
            <CardContent className="p-4">
              <p className="text-xs font-semibold uppercase text-slate-500">Categorías</p>
              <p className="text-2xl font-bold text-slate-900 mt-1 font-['Barlow_Condensed']">
                {Object.keys(materialsByCategory).length}
              </p>
            </CardContent>
          </Card>
          <Card className="border-slate-200">
            <CardContent className="p-4">
              <p className="text-xs font-semibold uppercase text-slate-500">Precio Promedio</p>
              <p className="text-2xl font-bold text-orange-500 mt-1 font-['Barlow_Condensed']">
                {formatCurrency(materials.reduce((sum, m) => sum + m.price, 0) / materials.length)}
              </p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Delete confirmation */}
      <Dialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-xl font-['Barlow_Condensed']">Eliminar Material</DialogTitle>
          </DialogHeader>
          <p className="text-slate-600">
            ¿Estás seguro de que quieres eliminar este material?
          </p>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDeleteId(null)}>
              Cancelar
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleDelete}
              data-testid="confirm-delete-material-btn"
            >
              Eliminar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
